import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtModal from "../../components/NtModal";
import NtModalConfirmAlert from "../../components/NtModalConfirmAlert";
import NtPageSection from "../../components/NtPageSection";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import NtUserModalRolePicker from "./NtUserModalRolePicker";
import { useTheme } from "../../context/ThemeContext";
import {
  deleteGroupFromUser,
  fetchGroupsForUser,
  fetchPermissionsForGroup,
} from "../../api/rbac";
import UserContext from "../../context/UserContext";
import { parseDisplayDate } from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";

/**
 * Roles & access — each assigned role renders as an expandable card. The
 * row's chevron toggles an inline panel that shows the role's permissions
 * grouped by module, so admins don't need a second modal. Permission counts
 * are fetched per role on mount and cached for the inline expansion.
 */
function NtUserRBAC({ containerStyle, userId }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const rolePickerRef = useRef();
  const confirmRef = useRef();
  const context = useContext(UserContext);
  const domain = context.user?.domain;

  const [data, setData] = useState([]);
  const [permsByRole, setPermsByRole] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const handleDataFetch = async () => {
    const response = await fetchGroupsForUser(
      domain,
      userId,
      1,
      10000,
      "",
      {},
    );
    Logger("User roles fetch", response);
    if (!response.ok) return;
    const roles = response.data?.data || [];
    setData(roles);

    const results = await Promise.all(
      roles.map((r) =>
        fetchPermissionsForGroup(domain, r.group_uuid, 1, 100000, "", {})
          .then((res) => ({
            id: r.group_uuid,
            perms: res?.ok ? res.data?.data || [] : [],
          }))
          .catch(() => ({ id: r.group_uuid, perms: [] })),
      ),
    );
    const map = {};
    for (const r of results) map[r.id] = r.perms;
    setPermsByRole(map);
  };

  const handleDelete = async () => {
    if (!pendingDeleteId) return;
    const response = await deleteGroupFromUser(
      domain,
      userId,
      pendingDeleteId,
    );
    if (response.ok) {
      if (expandedId === pendingDeleteId) setExpandedId(null);
      handleDataFetch();
    }
    setPendingDeleteId(null);
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    if (userId) handleDataFetch();
  }, [userId]);

  const manageBtn = (
    <NtTouchableEffect
      style={styles.addBtn}
      onPress={() => rolePickerRef.current?.show()}
    >
      <NtText style={styles.addText}>Manage roles</NtText>
    </NtTouchableEffect>
  );

  const trailing = (
    <View style={styles.trailing}>
      {data.length > 0 ? (
        <View style={styles.countBadge}>
          <NtText style={styles.countBadgeText}>{data.length}</NtText>
        </View>
      ) : null}
      {manageBtn}
    </View>
  );

  return (
    <NtPageSection
      title="Roles & access"
      trailing={trailing}
      style={containerStyle}
    >
      <View style={styles.body}>
        {data.length === 0 ? (
          <View style={styles.emptyWrap}>
            <View style={styles.emptyIcoCircle}>
              <NtMaterialIcon
                name="shield-account-outline"
                color={theme.textMute}
                size={20}
              />
            </View>
            <NtText style={styles.emptyText}>
              This user has no roles yet.
            </NtText>
            <View style={{ marginTop: 12 }}>{manageBtn}</View>
          </View>
        ) : (
          data.map((item) => {
            const id = item.group_uuid;
            const expanded = expandedId === id;
            const perms = permsByRole[id];
            const metaText = [
              item.group_description,
              item.insert_date
                ? `added ${parseDisplayDate(item.insert_date)}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <View key={item.user_group_uuid} style={styles.card}>
                <NtTouchableEffect
                  style={styles.head}
                  onPress={() => toggleExpand(id)}
                >
                  <View style={styles.ico}>
                    <NtText style={styles.icoText}>R</NtText>
                  </View>
                  <View style={styles.text}>
                    <View style={styles.nameLine}>
                      <NtText style={styles.name} numberOfLines={1}>
                        {item.group_name}
                      </NtText>
                      {perms != null ? (
                        <View style={styles.countPill}>
                          <NtText style={styles.countPillText}>
                            {perms.length}{" "}
                            {perms.length === 1 ? "perm" : "perms"}
                          </NtText>
                        </View>
                      ) : null}
                    </View>
                    {metaText ? (
                      <NtText style={styles.desc} numberOfLines={1}>
                        {metaText}
                      </NtText>
                    ) : null}
                  </View>
                  <NtMaterialIcon
                    name={expanded ? "chevron-up" : "chevron-down"}
                    color={theme.textMute}
                    size={16}
                  />
                  <NtTouchableEffect
                    style={styles.del}
                    onPress={() => {
                      setPendingDeleteId(item.group_uuid);
                      confirmRef.current?.show();
                    }}
                  >
                    <NtMaterialIcon
                      name="close"
                      color={theme.textDim}
                      size={14}
                    />
                  </NtTouchableEffect>
                </NtTouchableEffect>

                {expanded ? (
                  <View style={styles.permsWrap}>
                    {perms == null ? (
                      <View style={styles.loadingWrap}>
                        <NtActivityIndicator
                          color={theme.brand2}
                          size="small"
                        />
                      </View>
                    ) : (
                      <PermissionGroups permissions={perms} />
                    )}
                  </View>
                ) : null}
              </View>
            );
          })
        )}
      </View>

      <NtModal ref={rolePickerRef}>
        <NtUserModalRolePicker
          userId={userId}
          selections={data}
          onCancel={() => rolePickerRef.current?.dismiss()}
          onSaved={() => {
            rolePickerRef.current?.dismiss();
            handleDataFetch();
          }}
        />
      </NtModal>

      <NtModal ref={confirmRef}>
        <NtModalConfirmAlert
          title="Remove role"
          message="Are you sure you want to remove this role from the user?"
          onCancel={() => {
            confirmRef.current?.dismiss();
            setPendingDeleteId(null);
          }}
          onAccept={() => {
            confirmRef.current?.dismiss();
            handleDelete();
          }}
        />
      </NtModal>
    </NtPageSection>
  );
}

function PermissionGroups({ permissions }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makePermStyles(theme), [theme]);

  if (!permissions || permissions.length === 0) {
    return (
      <NtText style={styles.empty}>This role has no permissions.</NtText>
    );
  }

  const byModule = new Map();
  for (const p of permissions) {
    const name = p.permission_name || p.name || "";
    const parts = name.split(".");
    const moduleKey = parts.slice(0, -1).join(".") || name;
    const action = parts[parts.length - 1] || "—";
    if (!byModule.has(moduleKey)) byModule.set(moduleKey, new Set());
    byModule.get(moduleKey).add(action);
  }

  return (
    <View>
      {[...byModule.entries()].map(([mod, actions]) => (
        <View key={mod} style={styles.group}>
          <NtText style={styles.module}>{mod}</NtText>
          <View style={styles.chips}>
            {[...actions].map((a) => (
              <View key={a} style={styles.chip}>
                <NtText style={styles.chipText}>{a}</NtText>
              </View>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    body: { padding: 8 },
    trailing: { flexDirection: "row", alignItems: "center", gap: 8 },
    countBadge: {
      paddingHorizontal: 7,
      paddingVertical: 1,
      borderRadius: 999,
      backgroundColor: t.surface3,
      borderWidth: 1,
      borderColor: t.borderSoft,
    },
    countBadgeText: {
      color: t.textDim,
      fontSize: 10,
      fontWeight: "600",
    },
    addBtn: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 4,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
    },
    addText: { fontSize: 11, color: t.brand2, fontWeight: "600" },
    card: {
      backgroundColor: t.surface,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
      marginBottom: 6,
      overflow: "hidden",
    },
    head: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingVertical: 10,
      paddingHorizontal: 12,
    },
    ico: {
      width: 28,
      height: 28,
      borderRadius: 6,
      backgroundColor: "rgba(76,183,130,0.15)",
      borderWidth: 1,
      borderColor: "rgba(76,183,130,0.30)",
      alignItems: "center",
      justifyContent: "center",
    },
    icoText: { color: t.green, fontSize: 11, fontWeight: "700" },
    text: { flex: 1, minWidth: 0 },
    nameLine: { flexDirection: "row", alignItems: "center", gap: 8 },
    name: { color: t.text, fontSize: 13, fontWeight: "600", flexShrink: 1 },
    countPill: {
      paddingHorizontal: 7,
      paddingVertical: 1,
      borderRadius: 999,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
    },
    countPillText: {
      color: t.brand2,
      fontSize: 10,
      fontWeight: "700",
      letterSpacing: 0.3,
    },
    desc: { color: t.textDim, fontSize: 11, marginTop: 1 },
    del: { padding: 4, borderRadius: 4 },
    permsWrap: {
      paddingHorizontal: 12,
      paddingTop: 4,
      paddingBottom: 12,
      borderTopWidth: 1,
      borderTopColor: t.borderSoft,
      backgroundColor: t.surface2,
    },
    loadingWrap: { paddingVertical: 12, alignItems: "center" },
    emptyWrap: {
      alignItems: "center",
      paddingVertical: 24,
      paddingHorizontal: 12,
    },
    emptyIcoCircle: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: t.surface3,
      borderWidth: 1,
      borderColor: t.borderSoft,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 8,
    },
    emptyText: { color: t.textMute, fontSize: 12 },
  });

const makePermStyles = (t) =>
  StyleSheet.create({
    group: { paddingVertical: 6 },
    module: { color: t.textDim, fontSize: 11, marginBottom: 4 },
    chips: { flexDirection: "row", flexWrap: "wrap", gap: 4 },
    chip: {
      paddingHorizontal: 7,
      paddingVertical: 2,
      borderRadius: 3,
      borderWidth: 1,
      borderColor: t.pillBorder,
      backgroundColor: t.pillBg,
    },
    chipText: { color: t.brand2, fontSize: 10, fontWeight: "600" },
    empty: { color: t.textMute, fontSize: 12, fontStyle: "italic" },
  });

export default NtUserRBAC;
